export type AttributeSelectionMode = 'single' | 'multiple' | 'quantity';
export type AttributePresentation = 'auto' | 'chips' | 'dropdown' | 'image_cards';

export interface NormalizedAttributeOption {
  label: string;
  value: string;
  image?: string;
  priceAdjust?: number;
  legacyValue?: string;
}

export interface NormalizedProductAttribute {
  name: string;
  slug: string;
  legacyIndex: number;
  selection: AttributeSelectionMode;
  presentation: AttributePresentation;
  required: boolean;
  options: NormalizedAttributeOption[];
  legacy?: { type: string; obj: string };
}

export type AttributeSelectionValue = string | string[] | Record<string, number>;
export type AttributeSelections = Record<string, AttributeSelectionValue>;

function arrayAt<T>(value: unknown, index: number): T[] {
  return Array.isArray(value) && Array.isArray(value[index]) ? value[index] as T[] : [];
}

function slugPart(name: string): string {
  return name.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'attribute';
}

/** Convert legacy column-oriented product attributes to one stable runtime model. */
export function normalizeProductAttributes(raw: unknown): NormalizedProductAttribute[] {
  if (Array.isArray(raw)) {
    return raw.map((entry, index) => {
      const row = entry && typeof entry === 'object' ? entry as Record<string, unknown> : {};
      const type = String(row.type || 'selectbox');
      const obj = String(row.obj || 'none');
      const explicitPresentation = String(row.presentation || '');
      const titles = Array.isArray(row.titles) ? row.titles : [];
      const prices = Array.isArray(row.prices) ? row.prices : [];
      const values = Array.isArray(row.values) ? row.values : [];
      const name = String(row.name || `Attribute ${index + 1}`);
      return {
        name,
        slug: `attr-${index}-${slugPart(name)}`,
        legacyIndex: index,
        selection: type === 'checkbox' ? 'multiple' : type === 'textlist' || type === 'number' ? 'quantity' : 'single',
        presentation: isAttributePresentation(explicitPresentation)
          ? explicitPresentation
          : obj === 'image' ? 'image_cards' : type === 'radio' ? 'chips' : 'auto',
        required: row.required === 1 || row.required === '1' || row.required === true,
        options: titles.map((title, optionIndex) => ({
          label: String(title ?? ''),
          value: String(optionIndex),
          priceAdjust: Number(prices[optionIndex]) || 0,
          ...(obj === 'image' && values[optionIndex] ? { image: String(values[optionIndex]) } : {}),
          ...(values[optionIndex] != null ? { legacyValue: String(values[optionIndex]) } : {}),
        })),
        legacy: { type, obj },
      } satisfies NormalizedProductAttribute;
    });
  }
  if (!raw || typeof raw !== 'object') return [];
  const source = raw as Record<string, unknown>;
  if (Array.isArray(source.items)) {
    return (source.items as NormalizedProductAttribute[]).filter((item) => item && Array.isArray(item.options));
  }
  const names = Array.isArray(source.name) ? source.name : [];
  const types = Array.isArray(source.type) ? source.type : [];
  const objects = Array.isArray(source.obj) ? source.obj : [];
  const presentations = Array.isArray(source.presentation) ? source.presentation : [];
  const required = Array.isArray(source.required) ? source.required : [];
  return names.map((rawName, index) => {
    const name = String(rawName || `Attribute ${index + 1}`);
    const type = String(types[index] || 'selectbox');
    const obj = String(objects[index] || 'none');
    const titles = arrayAt<unknown>(source.titles, index);
    const prices = arrayAt<unknown>(source.prices, index);
    const values = arrayAt<unknown>(source.value, index);
    const selection: AttributeSelectionMode = type === 'checkbox'
      ? 'multiple'
      : type === 'textlist'
        ? 'quantity'
        : 'single';
    const explicitPresentation = String(presentations[index] || '');
    const presentation: AttributePresentation = isAttributePresentation(explicitPresentation)
      ? explicitPresentation
      : obj === 'image'
        ? 'image_cards'
        : type === 'radio'
          ? 'chips'
          : 'auto';
    return {
      name,
      slug: `attr-${index}-${slugPart(name)}`,
      legacyIndex: index,
      selection,
      presentation,
      required: required[index] === 1 || required[index] === '1' || required[index] === true,
      options: titles.map((title, optionIndex) => ({
        label: String(title ?? ''),
        value: String(optionIndex),
        priceAdjust: Number(prices[optionIndex]) || 0,
        ...(obj === 'image' && values[optionIndex] ? { image: String(values[optionIndex]) } : {}),
        ...(values[optionIndex] != null ? { legacyValue: String(values[optionIndex]) } : {}),
      })),
      legacy: { type, obj },
    };
  });
}

function isAttributePresentation(value: string): value is AttributePresentation {
  return value === 'auto' || value === 'chips' || value === 'dropdown' || value === 'image_cards';
}

export function serializeLegacyOptionsParam(
  attributes: NormalizedProductAttribute[],
  selections: AttributeSelections,
): string {
  const tokens: string[] = [];
  for (const attr of attributes) {
    const value = selections[attr.slug];
    if (typeof value === 'string' && value !== '') tokens.push(`${attr.legacyIndex}_${value}`);
    if (Array.isArray(value)) {
      value.forEach((option) => tokens.push(`${attr.legacyIndex}_${option}_${option}`));
    } else if (value && typeof value === 'object') {
      Object.entries(value).forEach(([option, qty]) => {
        if (Number(qty) > 0) tokens.push(`${attr.legacyIndex}_${option}_${Number(qty)}`);
      });
    }
  }
  return tokens.join('-');
}

export function parseLegacyAttributeOptions(raw: string, attributes: NormalizedProductAttribute[]): AttributeSelections {
  const selections: AttributeSelections = {};
  for (const token of String(raw || '').split('-')) {
    const match = token.match(/^(\d+)(?:_(\d+))?_(.+)$/) || token.match(/^(\d+)_(.+)$/);
    if (!match) continue;
    const index = Number(match[1]);
    const attr = attributes.find((item) => item.legacyIndex === index);
    if (!attr) continue;
    const value = match.length === 4 ? match[3] : match[2];
    if (attr.selection === 'multiple') {
      const current = Array.isArray(selections[attr.slug]) ? selections[attr.slug] as string[] : [];
      if (!current.includes(value)) selections[attr.slug] = [...current, value];
    } else if (attr.selection === 'quantity') {
      // `{index}_{option}_{qty}` from serializeLegacyOptionsParam().
      const qty = Number(value);
      if (match.length !== 4 || match[2] === undefined || !(qty > 0)) continue;
      const current = selections[attr.slug];
      const quantities = current && typeof current === 'object' && !Array.isArray(current) ? current : {};
      selections[attr.slug] = { ...quantities, [match[2]]: qty };
    } else {
      selections[attr.slug] = value;
    }
  }
  return selections;
}

/** Shape expected by legacy DesignerPriceCalculator::getPriceAttributes(). */
export function serializeAttributeSelections(
  attributes: NormalizedProductAttribute[],
  selections: AttributeSelections,
): Record<string, string | Record<string, string | number>> {
  const out: Record<string, string | Record<string, string | number>> = {};
  for (const attr of attributes) {
    const value = selections[attr.slug];
    if (attr.selection === 'single') {
      if (typeof value === 'string' && value !== '') out[String(attr.legacyIndex)] = value;
      continue;
    }
    if (attr.selection === 'multiple') {
      const picked = Array.isArray(value) ? value : [];
      if (picked.length) {
        out[String(attr.legacyIndex)] = Object.fromEntries(picked.map((option) => [option, option]));
      }
      continue;
    }
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      const quantities = Object.fromEntries(
        Object.entries(value).filter(([, qty]) => Number(qty) > 0).map(([key, qty]) => [key, Number(qty)]),
      );
      if (Object.keys(quantities).length) out[String(attr.legacyIndex)] = quantities;
    }
  }
  return out;
}

export function missingRequiredAttributes(
  attributes: NormalizedProductAttribute[],
  selections: AttributeSelections,
): NormalizedProductAttribute[] {
  return attributes.filter((attr) => {
    if (!attr.required) return false;
    const value = selections[attr.slug];
    if (typeof value === 'string') return value === '';
    if (Array.isArray(value)) return value.length === 0;
    if (value && typeof value === 'object') return !Object.values(value).some((qty) => Number(qty) > 0);
    return true;
  });
}
